import React from "react";
import { BatteryFull, BatteryMedium, BatteryLow } from "lucide-react";
import { LvReplacementForm } from "./LvReplacementForm";
import { LvHistoryLog } from "./LvHistoryLog";

const COLOR = { green: "#39FF14", amber: "#FFB020", red: "#FF5C5C", cyan: "#00E5FF" };

/** Low-voltage battery monitor — heuristic health model.
 *  Platform voltage comes from the owner's Tesla OAuth vehicle data:
 *  Model S / X / Cybertruck run a 16V lithium-ion LV system (always);
 *  Model 3 / Y run 12V — li-ion from 2022+, lead-acid/AGM before
 *  (~4-5yr service life, shortened by heat). */
export const is16vModel = (model) => {
  const norm = String(model || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return norm.includes("models") || norm.includes("modelx") || norm.includes("cybertruck");
};

export const lowVoltageHealth = (vehicle, climate, replacedAt = null, chemistryOverride = null) => {
  const year = vehicle.year || new Date().getFullYear() - 3;
  const sixteenV = is16vModel(vehicle.model);
  // 16V models are always lithium-ion per platform; 3/Y default by year.
  const liIon = sixteenV ? true : (chemistryOverride ? chemistryOverride === "li-ion" : year >= 2022);
  const voltClass = sixteenV ? 16 : 12;
  const anchor = replacedAt ? new Date(replacedAt).getTime() : new Date(year, 5, 1).getTime();
  const ageYears = Math.max(0.05, (Date.now() - anchor) / (365.25 * 86400e3));
  const summerHigh = climate?.summer_high_f || 88;
  // Heat accelerates lead-acid wear sharply; li-ion far less so.
  const heatFactor = 1 + Math.max(0, (summerHigh - 85) / 100) * (liIon ? 0.6 : 2.2);
  const serviceLife = liIon ? 12 : 5; // years
  const wearPct = Math.min(100, (ageYears * heatFactor / serviceLife) * 100);
  const health = Math.max(5, Math.round(100 - wearPct));
  const yearsLeft = Math.max(0, (serviceLife / heatFactor) - ageYears);

  // MEASURED mode — live telemetry voltage wins over the age model.
  const v = typeof vehicle.lvVoltage === "number" ? vehicle.lvVoltage : null;
  let status;
  if (v != null) {
    if (voltClass === 16) status = v >= 15.2 ? "Healthy" : v >= 14.4 ? "Monitor" : "Replace";
    else if (liIon) status = v >= 13.0 ? "Healthy" : v >= 12.6 ? "Monitor" : "Replace";
    else status = v >= 12.4 ? "Healthy" : v >= 12.0 ? "Monitor" : "Replace";
  } else {
    status = health >= 60 ? "Healthy" : health >= 35 ? "Monitor" : "Replace";
  }
  return {
    health, status, liIon, sixteenV, voltClass, ageYears, yearsLeft,
    voltage: v, measured: v != null, replaced: !!replacedAt,
    chemistry: liIon ? `${voltClass}V lithium-ion` : "12V lead-acid (AGM)",
  };
};

const STATUS_STYLE = {
  Healthy: { color: COLOR.green, bg: "rgba(57,255,20,0.10)", border: "rgba(57,255,20,0.35)" },
  Monitor: { color: COLOR.amber, bg: "rgba(255,176,32,0.10)", border: "rgba(255,176,32,0.35)" },
  Replace: { color: COLOR.red, bg: "rgba(255,92,92,0.10)", border: "rgba(255,92,92,0.4)" },
};

const STATUS_TIP = {
  Healthy: "No action needed — EVIQ re-scores this monthly as the unit ages.",
  Monitor: "Approaching typical end of life. Replace proactively — the low-voltage warning often gives only days of notice before a no-start.",
  Replace: "Past typical service life. Book a replacement now to avoid a stranded no-start — it's a quick, low-cost service item.",
};

const STATUS_ICON = { Healthy: BatteryFull, Monitor: BatteryMedium, Replace: BatteryLow };

export const LowVoltageCard = ({ vehicle, climate }) => {
  const [replacedAt, setReplacedAt] = React.useState(null);
  const [chem, setChem] = React.useState(null);
  const [historyKey, setHistoryKey] = React.useState(0);
  const [formKey, setFormKey] = React.useState(0);
  const vin = vehicle?.vin && !String(vehicle.vin).includes("•") ? String(vehicle.vin).toUpperCase() : null;

  const lv = lowVoltageHealth(vehicle, climate, replacedAt, chem);
  const s = STATUS_STYLE[lv.status];
  const StatusIcon = STATUS_ICON[lv.status] || BatteryMedium;
  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
      data-testid="dna-low-voltage-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0 eviq-status-pulse"
          style={{
            background: s.bg,
            borderColor: s.border,
            boxShadow: `0 0 12px ${s.color}55`,
          }}
          data-testid="lv-battery-icon"
        >
          <StatusIcon className="w-5 h-5" style={{ color: s.color }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Low-voltage battery
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            {lv.chemistry}
          </div>
        </div>
        <span
          className="px-3 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wide shrink-0"
          style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}
          data-testid="lv-status-pill"
        >
          {lv.status}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/5">
        <div>
          <div className="font-display text-xl text-white font-semibold leading-none">
            {lv.measured
              ? <>{lv.voltage.toFixed(2)}<span className="font-mono text-[10px] text-zinc-400">V</span></>
              : <>{lv.health}<span className="font-mono text-[10px] text-zinc-400">%</span></>}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider mt-1"
               style={{ color: lv.measured ? COLOR.cyan : "#71717a" }} data-testid="lv-source-label">
            {lv.measured ? "measured · live telemetry" : "est. condition · age model"}
          </div>
        </div>
        <div>
          <div className="font-display text-xl text-white font-semibold leading-none">
            {lv.yearsLeft >= 1 ? `~${lv.yearsLeft.toFixed(1)}y` : `~${Math.max(0, Math.round(lv.yearsLeft * 12))}mo`}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
            expected life left
          </div>
        </div>
      </div>
      <p className="font-mono text-[10px] text-zinc-400 leading-relaxed mt-3" data-testid="lv-status-tip">
        {STATUS_TIP[lv.status]}
      </p>
      {vin && (
        <div className="mt-2 pt-2 border-t border-white/5" data-testid="lv-replacement-section">
          {!lv.replaced && (
            <div className="font-mono text-[9px] uppercase tracking-wider mb-1" style={{ color: COLOR.cyan }}>
              Just replaced this battery? Log it to reset the model
            </div>
          )}
          <LvReplacementForm
            vin={vin}
            refreshKey={formKey}
            sixteenV={lv.sixteenV}
            liIonLabel={lv.sixteenV ? "Lithium-ion 16V" : "Lithium-ion 12V"}
            onChange={(rec) => {
              setReplacedAt(rec?.replaced_at || null);
              setChem(rec?.chemistry || null);
              setHistoryKey((k) => k + 1);
            }}
          />
          <LvHistoryLog
            vin={vin}
            refreshKey={historyKey}
            canEdit
            liIonLabel={lv.sixteenV ? "Lithium-ion 16V" : "Lithium-ion 12V"}
            onChanged={(current) => {
              setReplacedAt(current?.replaced_at || null);
              setChem(current?.chemistry || null);
              setFormKey((k) => k + 1);
            }}
          />
        </div>
      )}
    </div>
  );
};
